import React, { useEffect, useRef, useState } from "react";
import { X, Download, Share2 } from "lucide-react";
import { bareDomain } from "./listings";
import { drawShareCard, onEmblemReady } from "./shareCardCanvas";
import {
  SHARE_SITE,
  listingShareUrl,
  tweetIntentUrl,
  shareChrome,
  downloadCanvasPng,
} from "./shareLinks";

export function ShareCard({ listing, rank, onClose, siteOrigin = SHARE_SITE }) {
  const canvasRef = useRef(null);
  const chrome = shareChrome(rank);
  const domain = bareDomain(listing.url);
  const shareUrl = listingShareUrl(listing.id, siteOrigin);
  const tweet = tweetIntentUrl({ rank, domain, shareUrl, amount: listing.amount });
  const closerPct = (listing.amount / 10000) * 100;

  useEffect(() => {
    const draw = () =>
      drawShareCard(canvasRef.current, {
        rank,
        domain,
        amount: listing.amount,
        chrome,
        closerPct,
      });
    draw();
    // Re-draw once the emblem image finishes loading (async first paint).
    const off = onEmblemReady(draw);
    return off;
  }, [rank, domain, listing.amount, chrome, closerPct]);

  return (
    <div
      data-testid="share-card"
      data-chrome={chrome}
      className={`share-card share-card-chrome-${chrome} rounded-xl border bg-[#0b1120] p-4 text-left sm:p-5`}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <p className="text-lg font-bold tracking-wide text-white">I fueled Sam</p>
        {closerPct > 0 && (
          <span data-testid="share-closer-pct" className="mono text-[10px] tracking-[0.15em] text-gmh-cyan">
            +{closerPct < 1 ? closerPct.toFixed(2) : closerPct.toFixed(1)}% CLOSER
          </span>
        )}
        {onClose && (
          <button
            type="button"
            data-testid="share-card-close"
            onClick={onClose}
            className="text-slate-500 transition-colors hover:text-white"
            aria-label="Close share card"
          >
            <X size={16} />
          </button>
        )}
      </div>
      <canvas
        ref={canvasRef}
        data-testid="share-canvas"
        width={1200}
        height={630}
        className="mb-3 w-full rounded-md border border-[#1c2740]"
      />
      <p data-testid="share-url" className="mono break-all text-[11px] text-slate-400">
        {shareUrl}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <a
          data-testid="share-tweet"
          href={tweet}
          target="_blank"
          rel="noopener noreferrer"
          className="mono inline-flex items-center gap-2 rounded-md border border-gmh-cyan/40 bg-gmh-cyan/10 px-3 py-2 text-[11px] font-semibold tracking-wide text-gmh-cyan hover:bg-gmh-cyan/20"
        >
          Share on X
        </a>
        <button
          type="button"
          data-testid="share-download"
          onClick={() => downloadCanvasPng(canvasRef.current, "i-fueled-sam.png")}
          className="mono inline-flex items-center gap-2 rounded-md border border-slate-500/40 bg-slate-500/10 px-3 py-2 text-[11px] font-semibold tracking-wide text-slate-200 hover:bg-slate-500/20"
        >
          <Download size={12} /> Download PNG
        </button>
      </div>
    </div>
  );
}

export function ShareTrigger({ listing, rank, siteOrigin = SHARE_SITE }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid={`share-btn-${listing.id}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className="mono inline-flex shrink-0 items-center gap-1 rounded border border-slate-600/50 px-1.5 py-0.5 text-[9px] tracking-[0.18em] text-slate-400 hover:border-gmh-cyan/50 hover:text-gmh-cyan"
        aria-label="Share this listing"
      >
        <Share2 size={10} /> SHARE
      </button>
      {open && (
        <div
          data-testid="share-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-8"
          onClick={() => {
            // Backdrop click closes. No preventDefault/stopPropagation needed here —
            // inner clicks are stopped by the card wrapper below, and preventing
            // default on the backdrop would not matter (it has no default action).
            setOpen(false);
          }}
        >
          <div
            className="w-full max-w-lg"
            onClick={(e) => {
              // Keep clicks inside the card from bubbling to the backdrop's close.
              // Do NOT preventDefault() — that would cancel the Share-on-X anchor's
              // navigation (and any other link/button default) inside the card.
              e.stopPropagation();
            }}
          >
            <ShareCard
              listing={listing}
              rank={rank}
              siteOrigin={siteOrigin}
              onClose={() => setOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}
